import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Cpu, Sparkles, X, Search, Globe, Menu, Users, Play, 
  Download, ChevronDown, ChevronUp, Zap, Shield, Clock,
  TrendingUp, Target, Award, CheckCircle, ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

/* ============================================
   THEME & CONFIG
============================================ */
const theme = {
  name: "BYTRON",
  accent: "#C98225",
  accentLight: "#e5a84b",
  danger: "#d90647",
  bgFrom: "#07090f",
  bgTo: "#0b0f19",
};

const TELEGRAM = { 
  chat: "https://t.me/Bytron",
  channel: "https://t.me/Bytron_Channel",
  reviews: "https://t.me/bytronreview"
};

const INSTAGRAM = {
  url: "https://www.instagram.com/bytron_hack?igsh=YXhmY3dpc2Yxazg5",
};

/* ============================================
   TRANSLATIONS (MULTI-LANGUAGE SUPPORT)
============================================ */
const TRANSLATIONS = {
  en: {
    availableIn: "Aviator Predictor Available in",
    downloads: "Downloads",
    accuracy: "Accuracy",
    support: "Support",
    androidSupport: "Android 7+ Supported",
    aiPowered: "AI-Powered",
    instantPredictions: "Instant Predictions",
    multiPlatform: "Multi-Platform",
    liveAnalysis: "Live Analysis",
    securePrivate: "Secure & Private",
    lifetimeLicense: "Lifetime License",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "AI-Powered Game Analysis",
    heroDesc: "Advanced prediction algorithms for Aviator, Crash, LuckyJet, Mines and more. Works on 1win, 1xbet, Mostbet and 190+ countries.",
    downloadNow: "Download Now",
    tryDemo: "Try Demo",
    getStarted: "Get Started",
  },
  es: {
    availableIn: "Aviator Predictor Disponible en",
    downloads: "Descargas",
    accuracy: "Precisión",
    support: "Soporte",
    androidSupport: "Compatible con Android 7+",
    aiPowered: "Impulsado por IA",
    instantPredictions: "Predicciones Instantáneas",
    multiPlatform: "Multi-Plataforma",
    liveAnalysis: "Análisis en Vivo",
    securePrivate: "Seguro y Privado",
    lifetimeLicense: "Licencia de por Vida",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "Análisis de Juegos con IA",
    heroDesc: "Algoritmos avanzados de predicción para Aviator, Crash, LuckyJet, Mines y más. Funciona en 1win, 1xbet, Mostbet y más de 190 países.",
    downloadNow: "Descargar Ahora",
    tryDemo: "Probar Demo",
    getStarted: "Comenzar",
  },
  pt: {
    availableIn: "Aviator Predictor Disponível em",
    downloads: "Downloads",
    accuracy: "Precisão",
    support: "Suporte",
    androidSupport: "Suporta Android 7+",
    aiPowered: "Alimentado por IA",
    instantPredictions: "Previsões Instantâneas",
    multiPlatform: "Multi-Plataforma",
    liveAnalysis: "Análise ao Vivo",
    securePrivate: "Seguro e Privado",
    lifetimeLicense: "Licença Vitalícia",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "Análise de Jogos com IA",
    heroDesc: "Algoritmos avançados de previsão para Aviator, Crash, LuckyJet, Mines e mais. Funciona em 1win, 1xbet, Mostbet e mais de 190 países.",
    downloadNow: "Baixar Agora",
    tryDemo: "Testar Demo",
    getStarted: "Começar",
  },
  hi: {
    availableIn: "Aviator Predictor उपलब्ध है",
    downloads: "डाउनलोड",
    accuracy: "सटीकता",
    support: "सहायता",
    androidSupport: "Android 7+ समर्थित",
    aiPowered: "AI संचालित",
    instantPredictions: "त्वरित भविष्यवाणियां",
    multiPlatform: "मल्टी-प्लेटफॉर्म",
    liveAnalysis: "लाइव विश्लेषण",
    securePrivate: "सुरक्षित और निजी",
    lifetimeLicense: "आजीवन लाइसेंस",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "AI-संचालित गेम विश्लेषण",
    heroDesc: "Aviator, Crash, LuckyJet, Mines और अधिक के लिए उन्नत भविष्यवाणी एल्गोरिदम। 1win, 1xbet, Mostbet और 190+ देशों में काम करता है।",
    downloadNow: "अभी डाउनलोड करें",
    tryDemo: "डेमो आज़माएं",
    getStarted: "शुरू करें",
  },
  ru: {
    availableIn: "Aviator Predictor Доступен в",
    downloads: "Загрузки",
    accuracy: "Точность",
    support: "Поддержка",
    androidSupport: "Поддержка Android 7+",
    aiPowered: "На базе ИИ",
    instantPredictions: "Мгновенные Прогнозы",
    multiPlatform: "Мульти-Платформа",
    liveAnalysis: "Живой Анализ",
    securePrivate: "Безопасно и Приватно",
    lifetimeLicense: "Пожизненная Лицензия",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "Анализ Игр на базе ИИ",
    heroDesc: "Продвинутые алгоритмы прогнозирования для Aviator, Crash, LuckyJet, Mines и других. Работает на 1win, 1xbet, Mostbet и в 190+ странах.",
    downloadNow: "Скачать Сейчас",
    tryDemo: "Попробовать Демо",
    getStarted: "Начать",
  },
  ko: {
    availableIn: "Aviator Predictor 사용 가능",
    downloads: "다운로드",
    accuracy: "정확도",
    support: "지원",
    androidSupport: "Android 7+ 지원",
    aiPowered: "AI 기반",
    instantPredictions: "즉시 예측",
    multiPlatform: "멀티 플랫폼",
    liveAnalysis: "실시간 분석",
    securePrivate: "안전하고 프라이빗",
    lifetimeLicense: "평생 라이선스",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "AI 기반 게임 분석",
    heroDesc: "Aviator, Crash, LuckyJet, Mines 등을 위한 고급 예측 알고리즘. 1win, 1xbet, Mostbet 및 190개 이상의 국가에서 작동합니다.",
    downloadNow: "지금 다운로드",
    tryDemo: "데모 체험",
    getStarted: "시작하기",
  },
  it: {
    availableIn: "Aviator Predictor Disponibile in",
    downloads: "Download",
    accuracy: "Precisione",
    support: "Supporto",
    androidSupport: "Supporta Android 7+",
    aiPowered: "Alimentato da IA",
    instantPredictions: "Previsioni Istantanee",
    multiPlatform: "Multi-Piattaforma",
    liveAnalysis: "Analisi dal Vivo",
    securePrivate: "Sicuro e Privato",
    lifetimeLicense: "Licenza a Vita",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "Analisi Giochi con IA",
    heroDesc: "Algoritmi avanzati di previsione per Aviator, Crash, LuckyJet, Mines e altro. Funziona su 1win, 1xbet, Mostbet e oltre 190 paesi.",
    downloadNow: "Scarica Ora",
    tryDemo: "Prova Demo",
    getStarted: "Inizia",
  },
  ja: {
    availableIn: "Aviator Predictor 利用可能",
    downloads: "ダウンロード",
    accuracy: "精度",
    support: "サポート",
    androidSupport: "Android 7+対応",
    aiPowered: "AI搭載",
    instantPredictions: "即座の予測",
    multiPlatform: "マルチプラットフォーム",
    liveAnalysis: "ライブ分析",
    securePrivate: "安全でプライベート",
    lifetimeLicense: "永久ライセンス",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "AI搭載ゲーム分析",
    heroDesc: "Aviator、Crash、LuckyJet、Minesなどの高度な予測アルゴリズム。1win、1xbet、Mostbetおよび190以上の国で動作します。",
    downloadNow: "今すぐダウンロード",
    tryDemo: "デモを試す",
    getStarted: "始める",
  },
  fr: {
    availableIn: "Aviator Predictor Disponible en",
    downloads: "Téléchargements",
    accuracy: "Précision",
    support: "Support",
    androidSupport: "Android 7+ Supporté",
    aiPowered: "Alimenté par IA",
    instantPredictions: "Prédictions Instantanées",
    multiPlatform: "Multi-Plateforme",
    liveAnalysis: "Analyse en Direct",
    securePrivate: "Sécurisé et Privé",
    lifetimeLicense: "Licence à Vie",
    heroTitle: "Aviator Predictor",
    heroSubtitle: "Analyse de Jeux par IA",
    heroDesc: "Algorithmes de prédiction avancés pour Aviator, Crash, LuckyJet, Mines et plus. Fonctionne sur 1win, 1xbet, Mostbet et plus de 190 pays.",
    downloadNow: "Télécharger Maintenant",
    tryDemo: "Essayer Démo",
    getStarted: "Commencer",
  },
};

// Map country codes to languages
const COUNTRY_TO_LANGUAGE = {
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es',
  BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt',
  IN: 'hi', NP: 'hi',
  RU: 'ru', BY: 'ru', KZ: 'ru', UA: 'ru', UZ: 'ru', KG: 'ru',
  KR: 'ko',
  IT: 'it', SM: 'it', VA: 'it',
  JP: 'ja',
  FR: 'fr', BE: 'fr', CH: 'fr', CA: 'fr', SN: 'fr', CI: 'fr', ML: 'fr', BF: 'fr', NE: 'fr', TG: 'fr', BJ: 'fr', GA: 'fr', CG: 'fr', CM: 'fr', MG: 'fr', HT: 'fr',
};

// Get language from country code
const getLanguageFromCountry = (countryCode) => {
  return COUNTRY_TO_LANGUAGE[countryCode] || 'en';
};

// Get translation
const getTranslation = (lang, key) => {
  return TRANSLATIONS[lang]?.[key] || TRANSLATIONS.en[key];
};

/* ============================================
   FALLING MONEY EFFECT
============================================ */
function FallingMoney() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const initialItems = Array.from({ length: 25 }, (_, i) => createItem(i));
    setItems(initialItems);

    const interval = setInterval(() => {
      setItems(prev => {
        const filtered = prev.filter(item => item.createdAt > Date.now() - 15000);
        if (filtered.length < 30) {
          return [...filtered, createItem(Date.now())];
        }
        return filtered;
      });
    }, 600);

    return () => clearInterval(interval);
  }, []);

  function createItem(id) {
    const types = [
      { content: '$', size: 24, isBill: false },
      { content: '$', size: 32, isBill: false },
      { content: '$', size: 20, isBill: false },
      { content: '💵', size: 28, isBill: false },
      { content: '💰', size: 26, isBill: false },
      { content: '💲', size: 22, isBill: false },
      { content: '🪙', size: 20, isBill: false },
      { content: '$100', size: 12, isBill: true },
      { content: '$50', size: 11, isBill: true },
    ];
    
    const selected = types[Math.floor(Math.random() * types.length)];
    
    return {
      id,
      createdAt: Date.now(),
      left: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 10 + Math.random() * 15,
      opacity: 0.08 + Math.random() * 0.15,
      rotation: Math.random() * 30 - 15,
      ...selected,
    };
  }

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {items.map((item) => (
        <div
          key={item.id}
          className="absolute flex items-center justify-center"
          style={{
            left: `${item.left}%`,
            top: '-50px',
            fontSize: `${item.size}px`,
            opacity: item.opacity,
            color: item.isBill ? '#90EE90' : '#C98225',
            background: item.isBill ? 'linear-gradient(135deg, #1a4d1a 0%, #0d260d 100%)' : 'transparent',
            padding: item.isBill ? '4px 8px' : '0',
            borderRadius: item.isBill ? '4px' : '0',
            border: item.isBill ? '1px solid rgba(144,238,144,0.3)' : 'none',
            fontWeight: item.isBill ? 'bold' : 'normal',
            textShadow: item.isBill ? 'none' : '0 0 10px rgba(201, 130, 37, 0.4)',
            animation: `
              moneyFall ${item.duration}s linear ${item.delay}s infinite,
              moneySway ${item.duration / 3}s ease-in-out ${item.delay}s infinite alternate
            `,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
          }}
        >
          {item.content}
        </div>
      ))}
      
      <style>{`
        @keyframes moneyFall {
          0% {
            transform: translateY(-50px) rotate(-15deg);
          }
          100% {
            transform: translateY(110vh) rotate(15deg);
          }
        }
        
        @keyframes moneySway {
          0% {
            margin-left: -30px;
          }
          100% {
            margin-left: 30px;
          }
        }
      `}</style>
    </div>
  );
}

/* ============================================
   UI HELPERS
============================================ */
const Button = ({ className = "", variant = "default", children, ...props }) => {
  const variants = {
    default: "bg-white/10 hover:bg-white/20",
    primary: `bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold`,
    danger: "bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500",
    outline: `border-2 border-amber-500 hover:bg-amber-500/20`,
  };
  
  return (
    <button
      className={`px-4 py-2 rounded-full transition-all duration-300 ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const Card = ({ className = "", children, glow = false, ...props }) => (
  <div
    className={`rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm ${glow ? 'shadow-[0_0_30px_rgba(201,130,37,0.15)]' : ''} ${className}`}
    {...props}
  >
    {children}
  </div>
);

const Grad = ({ children }) => (
  <span className="bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
    {children}
  </span>
);

const SectionTitle = ({ subtitle, children }) => (
  <div className="text-center mb-8 sm:mb-12">
    {subtitle && (
      <p className="text-amber-400 text-sm font-medium mb-2 uppercase tracking-wider">{subtitle}</p>
    )}
    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">{children}</h2>
  </div>
);

/* ============================================
   COUNTRIES DATA
============================================ */
const COUNTRIES = [
  { name: "Afghanistan", code: "AF" },
  { name: "Albania", code: "AL" },
  { name: "Algeria", code: "DZ" },
  { name: "Andorra", code: "AD" },
  { name: "Angola", code: "AO" },
  { name: "Antigua and Barbuda", code: "AG" },
  { name: "Argentina", code: "AR" },
  { name: "Armenia", code: "AM" },
  { name: "Australia", code: "AU" },
  { name: "Austria", code: "AT" },
  { name: "Azerbaijan", code: "AZ" },
  { name: "Bahamas", code: "BS" },
  { name: "Bahrain", code: "BH" },
  { name: "Bangladesh", code: "BD" },
  { name: "Barbados", code: "BB" },
  { name: "Belarus", code: "BY" },
  { name: "Belgium", code: "BE" },
  { name: "Belize", code: "BZ" },
  { name: "Benin", code: "BJ" },
  { name: "Bhutan", code: "BT" },
  { name: "Bolivia", code: "BO" },
  { name: "Bosnia and Herzegovina", code: "BA" },
  { name: "Botswana", code: "BW" },
  { name: "Brazil", code: "BR" },
  { name: "Brunei", code: "BN" },
  { name: "Bulgaria", code: "BG" },
  { name: "Burkina Faso", code: "BF" },
  { name: "Burundi", code: "BI" },
  { name: "Cabo Verde", code: "CV" },
  { name: "Cambodia", code: "KH" },
  { name: "Cameroon", code: "CM" },
  { name: "Canada", code: "CA" },
  { name: "Central African Republic", code: "CF" },
  { name: "Chad", code: "TD" },
  { name: "Chile", code: "CL" },
  { name: "China", code: "CN" },
  { name: "Colombia", code: "CO" },
  { name: "Comoros", code: "KM" },
  { name: "Congo (Democratic Republic)", code: "CD" },
  { name: "Congo (Republic)", code: "CG" },
  { name: "Costa Rica", code: "CR" },
  { name: "Côte d'Ivoire", code: "CI" },
  { name: "Croatia", code: "HR" },
  { name: "Cuba", code: "CU" },
  { name: "Cyprus", code: "CY" },
  { name: "Czech Republic", code: "CZ" },
  { name: "Denmark", code: "DK" },
  { name: "Djibouti", code: "DJ" },
  { name: "Dominica", code: "DM" },
  { name: "Dominican Republic", code: "DO" },
  { name: "Ecuador", code: "EC" },
  { name: "Egypt", code: "EG" },
  { name: "El Salvador", code: "SV" },
  { name: "Equatorial Guinea", code: "GQ" },
  { name: "Eritrea", code: "ER" },
  { name: "Estonia", code: "EE" },
  { name: "Eswatini", code: "SZ" },
  { name: "Ethiopia", code: "ET" },
  { name: "Fiji", code: "FJ" },
  { name: "Finland", code: "FI" },
  { name: "France", code: "FR" },
  { name: "Gabon", code: "GA" },
  { name: "Gambia", code: "GM" },
  { name: "Georgia", code: "GE" },
  { name: "Germany", code: "DE" },
  { name: "Ghana", code: "GH" },
  { name: "Greece", code: "GR" },
  { name: "Grenada", code: "GD" },
  { name: "Guatemala", code: "GT" },
  { name: "Guinea", code: "GN" },
  { name: "Guinea-Bissau", code: "GW" },
  { name: "Guyana", code: "GY" },
  { name: "Haiti", code: "HT" },
  { name: "Holy See (Vatican City)", code: "VA" },
  { name: "Honduras", code: "HN" },
  { name: "Hungary", code: "HU" },
  { name: "Iceland", code: "IS" },
  { name: "India", code: "IN" },
  { name: "Indonesia", code: "ID" },
  { name: "Iran", code: "IR" },
  { name: "Iraq", code: "IQ" },
  { name: "Ireland", code: "IE" },
  { name: "Israel", code: "IL" },
  { name: "Italy", code: "IT" },
  { name: "Jamaica", code: "JM" },
  { name: "Japan", code: "JP" },
  { name: "Jordan", code: "JO" },
  { name: "Kazakhstan", code: "KZ" },
  { name: "Kenya", code: "KE" },
  { name: "Kiribati", code: "KI" },
  { name: "Korea (North)", code: "KP" },
  { name: "Korea (South)", code: "KR" },
  { name: "Kosovo", code: "XK" },
  { name: "Kuwait", code: "KW" },
  { name: "Kyrgyzstan", code: "KG" },
  { name: "Laos", code: "LA" },
  { name: "Latvia", code: "LV" },
  { name: "Lebanon", code: "LB" },
  { name: "Lesotho", code: "LS" },
  { name: "Liberia", code: "LR" },
  { name: "Libya", code: "LY" },
  { name: "Liechtenstein", code: "LI" },
  { name: "Lithuania", code: "LT" },
  { name: "Luxembourg", code: "LU" },
  { name: "Madagascar", code: "MG" },
  { name: "Malawi", code: "MW" },
  { name: "Malaysia", code: "MY" },
  { name: "Maldives", code: "MV" },
  { name: "Mali", code: "ML" },
  { name: "Malta", code: "MT" },
  { name: "Marshall Islands", code: "MH" },
  { name: "Mauritania", code: "MR" },
  { name: "Mauritius", code: "MU" },
  { name: "Mexico", code: "MX" },
  { name: "Micronesia", code: "FM" },
  { name: "Moldova", code: "MD" },
  { name: "Monaco", code: "MC" },
  { name: "Mongolia", code: "MN" },
  { name: "Montenegro", code: "ME" },
  { name: "Morocco", code: "MA" },
  { name: "Mozambique", code: "MZ" },
  { name: "Myanmar", code: "MM" },
  { name: "Namibia", code: "NA" },
  { name: "Nauru", code: "NR" },
  { name: "Nepal", code: "NP" },
  { name: "Netherlands", code: "NL" },
  { name: "New Zealand", code: "NZ" },
  { name: "Nicaragua", code: "NI" },
  { name: "Niger", code: "NE" },
  { name: "Nigeria", code: "NG" },
  { name: "North Macedonia", code: "MK" },
  { name: "Norway", code: "NO" },
  { name: "Oman", code: "OM" },
  { name: "Pakistan", code: "PK" },
  { name: "Palau", code: "PW" },
  { name: "Palestine", code: "PS" },
  { name: "Panama", code: "PA" },
  { name: "Papua New Guinea", code: "PG" },
  { name: "Paraguay", code: "PY" },
  { name: "Peru", code: "PE" },
  { name: "Philippines", code: "PH" },
  { name: "Poland", code: "PL" },
  { name: "Portugal", code: "PT" },
  { name: "Qatar", code: "QA" },
  { name: "Romania", code: "RO" },
  { name: "Russia", code: "RU" },
  { name: "Rwanda", code: "RW" },
  { name: "Saint Kitts and Nevis", code: "KN" },
  { name: "Saint Lucia", code: "LC" },
  { name: "Saint Vincent and the Grenadines", code: "VC" },
  { name: "Samoa", code: "WS" },
  { name: "San Marino", code: "SM" },
  { name: "Sao Tome and Principe", code: "ST" },
  { name: "Saudi Arabia", code: "SA" },
  { name: "Senegal", code: "SN" },
  { name: "Serbia", code: "RS" },
  { name: "Seychelles", code: "SC" },
  { name: "Sierra Leone", code: "SL" },
  { name: "Singapore", code: "SG" },
  { name: "Slovakia", code: "SK" },
  { name: "Slovenia", code: "SI" },
  { name: "Solomon Islands", code: "SB" },
  { name: "Somalia", code: "SO" },
  { name: "South Africa", code: "ZA" },
  { name: "South Sudan", code: "SS" },
  { name: "Spain", code: "ES" },
  { name: "Sri Lanka", code: "LK" },
  { name: "Sudan", code: "SD" },
  { name: "Suriname", code: "SR" },
  { name: "Sweden", code: "SE" },
  { name: "Switzerland", code: "CH" },
  { name: "Syria", code: "SY" },
  { name: "Tajikistan", code: "TJ" },
  { name: "Tanzania", code: "TZ" },
  { name: "Thailand", code: "TH" },
  { name: "Timor-Leste", code: "TL" },
  { name: "Togo", code: "TG" },
  { name: "Tonga", code: "TO" },
  { name: "Trinidad and Tobago", code: "TT" },
  { name: "Tunisia", code: "TN" },
  { name: "Turkey", code: "TR" },
  { name: "Turkmenistan", code: "TM" },
  { name: "Tuvalu", code: "TV" },
  { name: "Uganda", code: "UG" },
  { name: "Ukraine", code: "UA" },
  { name: "United Arab Emirates", code: "AE" },
  { name: "United Kingdom", code: "GB" },
  { name: "United States", code: "US" },
  { name: "Uruguay", code: "UY" },
  { name: "Uzbekistan", code: "UZ" },
  { name: "Vanuatu", code: "VU" },
  { name: "Venezuela", code: "VE" },
  { name: "Vietnam", code: "VN" },
  { name: "Yemen", code: "YE" },
  { name: "Zambia", code: "ZM" },
  { name: "Zimbabwe", code: "ZW" },
];

/* ============================================
   HISTORY CHART
============================================ */
function HistoryChart() {
  const [data] = useState(() => {
    // Generate fake history data
    const arr = [];
    for (let i = 0; i < 30; i++) {
      arr.push({
        time: `${String(Math.floor(Math.random() * 24)).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
        multiplier: (Math.random() * 20 + 1).toFixed(2),
      });
    }
    return arr;
  });

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
      <SectionTitle subtitle="Live Statistics">
        Game <Grad>History</Grad>
      </SectionTitle>
      
      <Card className="p-4 sm:p-6 overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm text-white/60">Live Feed</span>
          </div>
          <div className="text-sm text-white/60">Last 30 rounds</div>
        </div>
        
        {/* Scrollable History */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {data.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`flex-shrink-0 p-3 rounded-lg text-center min-w-[70px] ${
                parseFloat(item.multiplier) >= 10 ? "bg-purple-500/20 border border-purple-500/30" :
                parseFloat(item.multiplier) >= 5 ? "bg-green-500/20 border border-green-500/30" :
                parseFloat(item.multiplier) >= 2 ? "bg-blue-500/20 border border-blue-500/30" :
                "bg-white/5 border border-white/10"
              }`}
            >
              <div className={`font-bold ${
                parseFloat(item.multiplier) >= 10 ? "text-purple-400" :
                parseFloat(item.multiplier) >= 5 ? "text-green-400" :
                parseFloat(item.multiplier) >= 2 ? "text-blue-400" :
                "text-white/70"
              }`}>
                {item.multiplier}x
              </div>
              <div className="text-xs text-white/40 mt-1">{item.time}</div>
            </motion.div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-white/10 text-xs">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-purple-500/50" />
            <span className="text-white/60">10x+ (Mega Win)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-green-500/50" />
            <span className="text-white/60">5x-10x (Big Win)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-blue-500/50" />
            <span className="text-white/60">2x-5x (Win)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded bg-white/20" />
            <span className="text-white/60">Below 2x</span>
          </div>
        </div>
      </Card>
    </section>
  );
}

/* ============================================
   HOW IT WORKS
============================================ */
function HowItWorks() {
  const steps = [
    {
      icon: <Download className="h-6 w-6" />,
      title: "1. Purchase & Download",
      desc: "Message @Bytron on Telegram, pick your tool, pay securely, and get instant download access."
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "2. Install & Activate",
      desc: "Install the APK on your Android device and enter your unique license key to unlock."
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "3. Launch & Analyze",
      desc: "Open BYTRON alongside your game app. Our AI immediately starts scanning game patterns."
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "4. Follow & Profit",
      desc: "Place bets based on our signals, cash out at predicted multipliers, and stack your winnings."
    },
  ];

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-20">
      <SectionTitle subtitle="4 Easy Steps">
        Getting <Grad>Started</Grad>
      </SectionTitle>
      
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {steps.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="p-6 h-full hover:border-amber-500/50 transition-colors">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-red-500/20 flex items-center justify-center text-amber-400 mb-4">
                {step.icon}
              </div>
              <h3 className="font-bold text-lg mb-2">{step.title}</h3>
              <p className="text-sm text-white/60">{step.desc}</p>
            </Card>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ============================================
   FAQ SECTION
============================================ */
function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      q: "What is Aviator Predictor APK and how does it work?",
      a: "Aviator Predictor APK is an advanced aviator hack and crash game predictor that analyzes game patterns in real-time. Our aviator signal app processes thousands of data points to predict crash points before each round, giving you an edge on platforms like 1win, 1xbet, and Stake."
    },
    {
      q: "Does this aviator hack work on Stake, 1win, and 1xbet?",
      a: "Yes! Our aviator predictor works as a stake aviator hack, 1win hack, and 1xbet hack. It supports stake crash hack, 1xbet crash hack, stake crash predictor, and all major platforms including Mostbet, Pin-Up, and 190+ others."
    },
    {
      q: "How do I download the aviator hack APK?",
      a: "To get the aviator hack download, simply message our Telegram @Bytron, select your crash game predictor package, complete payment, and receive instant download access. The aviator predictor APK installs in under 2 minutes on any Android 7+ device."
    },
    {
      q: "Is the stake aviator predictor safe to use?",
      a: "Absolutely. Our stake aviator hack and stake crash hack run locally on your device without touching game servers. The aviator signal app only reads publicly visible data, so your Stake, 1win, or 1xbet account stays completely safe."
    },
    {
      q: "What's the accuracy of this crash game predictor?",
      a: "Our aviator predictor APK maintains Best accuracy across all supported platforms. The 1xbet crash hack, stake crash predictor, and 1win hack modules are all optimized for maximum precision. Results depend on following our betting strategy correctly."
    },
    {
      q: "Which games does the aviator signal app support?",
      a: "Our crash game predictor supports Aviator, Crash, JetX, Lucky Jet, Mines, Thimbles, and Apple of Fortune. Works on Stake (stake aviator hack, stake crash hack), 1xbet (1xbet crash hack), 1win (1win hack), Mostbet, and more."
    },
    {
      q: "Can I use aviator hack on iPhone?",
      a: "Currently the aviator predictor APK is Android-only (Android 7+). For iOS users, many run our stake aviator predictor and crash game predictor on Android emulators. Native iOS version is in development."
    },
    {
      q: "Is there a subscription fee for the 1win hack or 1xbet hack?",
      a: "No! One payment gives you lifetime access to the aviator hack APK, stake crash predictor, 1xbet crash hack, and all features. Free updates included forever, plus 24/7 support via Telegram."
    },
  ];

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-20">
      <SectionTitle subtitle="Common Questions">
        Frequently Asked <Grad>Questions</Grad>
      </SectionTitle>
      
      <div className="max-w-3xl mx-auto space-y-3">
        {faqs.map((faq, i) => (
          <Card 
            key={i}
            className={`overflow-hidden transition-colors ${openIndex === i ? 'border-amber-500/50' : ''}`}
          >
            <button
              className="w-full p-4 sm:p-5 flex items-center justify-between text-left"
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            >
              <span className="font-medium pr-4">{faq.q}</span>
              {openIndex === i ? (
                <ChevronUp className="h-5 w-5 text-amber-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 text-white/40 flex-shrink-0" />
              )}
            </button>
            <AnimatePresence>
              {openIndex === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-white/70 text-sm leading-relaxed border-t border-white/10 pt-4">
                    {faq.a}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        ))}
      </div>
    </section>
  );
}

/* ============================================
   SERVER STATUS MODAL
============================================ */
function ServerStatusModal({ isOpen, onClose }) {
  const [search, setSearch] = useState("");

  if (!isOpen) return null;

  const filteredCountries = COUNTRIES.filter((country) =>
    country.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{ type: "tween", duration: 0.3 }}
        className="relative w-full sm:max-w-2xl max-h-[90vh] sm:max-h-[80vh] rounded-t-3xl sm:rounded-2xl border-t-2 sm:border-2 overflow-hidden"
        style={{ borderColor: theme.accent, backgroundColor: "#0a0c14" }}
      >
        <div className="sticky top-0 z-10 p-4 border-b border-white/10" style={{ backgroundColor: "#0a0c14" }}>
          <div className="flex justify-center mb-3 sm:hidden">
            <div className="w-10 h-1 rounded-full bg-white/30" />
          </div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-amber-400" />
              <h2 className="text-lg sm:text-xl font-bold">Server Availability</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
            <input
              type="text"
              placeholder="Search your country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/50 focus:outline-none focus:border-amber-400/50 text-base"
              style={{ fontSize: "16px" }}
            />
          </div>
          <div className="mt-3 flex items-center gap-4 text-sm text-white/60">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span>{COUNTRIES.length} servers online</span>
            </div>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(90vh-200px)] sm:max-h-[calc(80vh-180px)] p-4 overscroll-contain">
          <div className="grid grid-cols-1 gap-2">
            {filteredCountries.map((country) => (
              <div
                key={country.code}
                className="flex items-center justify-between p-3 rounded-xl bg-white/5 active:bg-white/10 sm:hover:bg-white/10 transition"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={`https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`}
                    alt={country.name}
                    className="w-6 h-4 object-cover rounded-sm"
                  />
                  <span className="text-sm">{country.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
                  <span className="text-xs text-green-400">Online</span>
                </div>
              </div>
            ))}
          </div>
          {filteredCountries.length === 0 && (
            <div className="text-center py-8 text-white/50">
              No countries found matching "{search}"
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

/* ============================================
   NAVIGATION
============================================ */
function GlassNav({ onOpenServerStatus }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "Demo", href: "#demo" },
    { label: "Catalog", href: "#catalog" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <>
      <nav className="fixed inset-x-0 top-0 z-40">
        <div className="mx-auto max-w-7xl px-3 py-2 sm:px-4 sm:py-3">
          <div
            className="flex items-center justify-between rounded-full border-2 backdrop-blur px-3 py-2 sm:px-4"
            style={{ borderColor: theme.accent, backgroundColor: "rgba(1,3,13,0.85)" }}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div
                className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-xl sm:rounded-2xl overflow-hidden"
                style={{
                  background: "radial-gradient(120% 120% at 50% 0%, rgba(201,130,37,.25), transparent)",
                  boxShadow: "0 0 25px -6px #C98225",
                  border: "2px solid rgba(201,130,37,.65)",
                }}
              >
                <img src="/logo.png" alt="BYTRON logo" className="absolute inset-0 h-full w-full object-cover" draggable="false" />
              </div>
              <div className="text-sm sm:text-base font-semibold tracking-wider">{theme.name}</div>
            </div>

            <div className="hidden items-center gap-2 md:flex">
              {navLinks.map((link) => (
                <a key={link.label} className="nav-btn" href={link.href}>{link.label}</a>
              ))}
              <button onClick={onOpenServerStatus} className="nav-btn flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                Servers
              </button>
              <Button variant="primary" onClick={() => window.open(TELEGRAM.chat, "_blank")}>
                Get Started
              </Button>
            </div>

            <button
              className="md:hidden p-2 -mr-1 rounded-full hover:bg-white/10 transition"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-72 md:hidden"
              style={{ backgroundColor: "#0a0c14" }}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <span className="font-semibold">Menu</span>
                  <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-full hover:bg-white/10 transition">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex-1 p-4 space-y-2">
                  {navLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block w-full p-3 rounded-xl text-left hover:bg-white/10 transition"
                    >
                      {link.label}
                    </a>
                  ))}
                  <button
                    onClick={() => { setMobileMenuOpen(false); onOpenServerStatus(); }}
                    className="flex items-center gap-3 w-full p-3 rounded-xl text-left hover:bg-white/10 transition"
                  >
                    <span className="h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse" />
                    Server Status
                  </button>
                </div>
                <div className="p-4 border-t border-white/10">
                  <button
                    onClick={() => window.open(TELEGRAM.chat, "_blank")}
                    className="w-full py-3 rounded-xl font-semibold transition bg-gradient-to-r from-amber-500 to-amber-600 text-black"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ============================================
   COUNTRY DETECTION BANNER
============================================ */
function CountryBanner() {
  const [userCountry, setUserCountry] = useState(null);
  const [lang, setLang] = useState('en');

  useEffect(() => {
    // Fetch user's country from IP
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        if (data.country_code && data.country_name) {
          setUserCountry({
            code: data.country_code,
            name: data.country_name
          });
          const detectedLang = getLanguageFromCountry(data.country_code);
          setLang(detectedLang);
        }
      })
      .catch(() => {
        // Fallback - don't show banner if detection fails
        setUserCountry(null);
      });
  }, []);

  if (!userCountry) return null;

  const t = (key) => getTranslation(lang, key);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="flex items-center justify-center gap-3 px-4 py-2 rounded-full border"
      style={{ 
        borderColor: 'rgba(34, 197, 94, 0.4)', 
        backgroundColor: 'rgba(34, 197, 94, 0.1)' 
      }}
    >
      <img
        src={`https://flagcdn.com/w40/${userCountry.code.toLowerCase()}.png`}
        alt={userCountry.name}
        className="w-6 h-4 object-cover rounded-sm shadow"
      />
      <span className="text-sm sm:text-base">
        <span className="text-green-400 font-medium">{t('availableIn')}</span>
        <span className="text-white font-bold ml-1">{userCountry.name}</span>
      </span>
      <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
    </motion.div>
  );
}

/* ============================================
   ACTIVE USERS COUNTER
============================================ */
function ActiveUsers() {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const startDate = new Date('2026-01-01').getTime();
    const now = Date.now();
    const daysPassed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
    const baseCount = 400 + Math.floor(daysPassed * 12.5);
    const variation = Math.floor(Math.random() * 20);
    const finalCount = baseCount + variation;
    
    let current = 0;
    const target = finalCount;
    const duration = 1500;
    const steps = 60;
    const increment = target / steps;
    const stepTime = duration / steps;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, stepTime);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="flex items-center justify-center"
    >
      <div 
        className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-full border"
        style={{ borderColor: 'rgba(201, 130, 37, 0.4)', backgroundColor: 'rgba(201, 130, 37, 0.1)' }}
      >
        <div className="relative">
          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400" />
          <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <span className="text-lg sm:text-xl font-bold text-white">{count.toLocaleString()}</span>
          <span className="text-xs sm:text-sm text-white/60">active users</span>
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================
   HERO
============================================ */
function Hero() {
  return (
    <section className="relative isolate mx-auto flex min-h-[85vh] sm:min-h-[92vh] max-w-7xl flex-col items-center justify-center gap-4 sm:gap-6 px-4 pt-20 sm:pt-28 pb-8 text-center">
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-sm"
        style={{ borderColor: theme.accent, backgroundColor: 'rgba(201, 130, 37, 0.1)' }}
      >
        <Sparkles className="h-4 w-4 text-amber-400" />
        <span>Aviator Predictor APK - Download Now</span>
      </motion.div>

      {/* Title */}
      <motion.h1 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-3xl sm:text-5xl md:text-7xl font-bold leading-tight"
      >
        <Grad>Aviator Predictor APK</Grad>
        <br />
        <span className="text-2xl sm:text-4xl md:text-5xl text-white/90">Aviator Hack & Crash Game Predictor</span>
      </motion.h1>

      {/* Subtitle */}
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mx-auto max-w-2xl text-white/70 text-sm sm:text-base md:text-lg"
      >
        Download the best aviator hack APK and crash game predictor. 
        Works as 1win hack, 1xbet hack, stake aviator hack, and stake crash predictor. 
        Get aviator signal app for 190+ countries.
      </motion.p>

      {/* CTA Buttons */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto"
      >
        <Button 
          variant="primary"
          className="w-full sm:w-auto px-8 py-3 text-lg flex items-center justify-center gap-2"
          onClick={() => window.open(TELEGRAM.chat, "_blank")}
        >
          <Download className="h-5 w-5" />
          Download Aviator Hack APK
        </Button>
        <Button 
          variant="outline"
          className="w-full sm:w-auto px-6 py-3"
          onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
        >
          Watch Demo
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </motion.div>

      {/* Country Banner */}
      <div className="mt-4">
        <CountryBanner />
      </div>

      {/* Active Users */}
      <div className="mt-3">
        <ActiveUsers />
      </div>

      {/* Trust Badges */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-white/50 text-xs sm:text-sm"
      >
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-green-400" />
          <span>Undetectable</span>
        </div>
        <div className="flex items-center gap-2">
          <Download className="h-4 w-4 text-purple-400" />
          <span>10K+ Active Users</span>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-400" />
          <span>High Win Rate</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-400" />
          <span>Instant Delivery</span>
        </div>
      </motion.div>
    </section>
  );
}

/* ============================================
   FEATURES
============================================ */
function Features() {
  const features = [
    {
      icon: <Target className="h-6 w-6" />,
      title: "Best Prediction Accuracy",
      desc: "Our aviator predictor APK and crash game predictor deliver industry-leading accuracy through real-time pattern analysis.",
    },
    {
      icon: <Download className="h-6 w-6" />,
      title: "10K+ Aviator Hack Downloads",
      desc: "Join thousands who use our aviator hack APK daily. Trusted aviator signal app for serious players.",
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Works on Stake, 1win, 1xbet",
      desc: "Full support for stake aviator hack, stake crash hack, 1win hack, 1xbet hack, 1xbet crash hack, and 190+ platforms.",
    },
    {
      icon: <Cpu className="h-6 w-6" />,
      title: "Aviator Hack APK for Android 7+",
      desc: "Download aviator predictor APK on any Android 7+ device. Stake crash predictor works on phones and tablets.",
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Undetectable Crash Predictor",
      desc: "Our stake aviator predictor runs locally. 1xbet crash hack and 1win hack never touch game servers - 100% safe.",
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Real-time Aviator Signals",
      desc: "Aviator signal app delivers predictions milliseconds before rounds. Perfect timing for stake crash hack wins.",
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "24/7 Support for All Hacks",
      desc: "Get help with aviator hack download, stake hack setup, 1xbet crash hack, or any crash game predictor issues.",
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "Lifetime Aviator Predictor Access",
      desc: "One payment for permanent aviator hack APK access. Includes stake aviator hack, 1win hack, all updates free.",
    },
  ];

  // Stats bar
  const stats = [
    { value: "10K+", label: "Downloads" },
    { value: "Best", label: "Accuracy" },
    { value: "190+", label: "Platforms" },
    { value: "9", label: "Languages" },
  ];

  return (
    <section id="features" className="mx-auto max-w-7xl px-4 py-12 sm:py-20">
      <SectionTitle subtitle="Why Choose Us">
        Powerful <Grad>Features</Grad>
      </SectionTitle>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        {stats.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="p-4 text-center">
              <div className="text-2xl sm:text-3xl font-bold text-amber-400">{stat.value}</div>
              <div className="text-xs sm:text-sm text-white/60">{stat.label}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {features.map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            viewport={{ once: true }}
          >
            <Card className="p-5 sm:p-6 h-full hover:border-amber-500/30 transition-colors group">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-red-500/20 flex items-center justify-center text-amber-400 mb-4 group-hover:scale-110 transition-transform">
                {f.icon}
              </div>
              <h3 className="font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-white/60">{f.desc}</p>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Supported Languages */}
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="mt-10 text-center"
      >
        <p className="text-white/60 text-sm mb-4">Supported Languages</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {[
            { code: 'us', name: 'English' },
            { code: 'es', name: 'Español' },
            { code: 'br', name: 'Português' },
            { code: 'in', name: 'हिंदी' },
            { code: 'ru', name: 'Русский' },
            { code: 'kr', name: '한국어' },
            { code: 'it', name: 'Italiano' },
            { code: 'jp', name: '日本語' },
            { code: 'fr', name: 'Français' },
          ].map((lang) => (
            <div key={lang.code} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <img
                src={`https://flagcdn.com/w20/${lang.code}.png`}
                alt={lang.name}
                className="w-5 h-3.5 object-cover rounded-sm"
              />
              <span className="text-xs text-white/80">{lang.name}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

/* ============================================
   CATALOG
============================================ */
function ProductCard({ title, price, image }) {
  return (
    <Link to={`/product/${title}`} className="block w-full cursor-pointer active:scale-[0.98] transition-transform">
      <div
        className="rounded-2xl border-2 sm:border-4 p-2 sm:p-3 transition hover:scale-[1.01] hover:border-amber-400"
        style={{ borderColor: theme.accent }}
      >
        <div className="overflow-hidden rounded-lg sm:rounded-xl">
          <div className="aspect-[16/9] relative bg-gradient-to-br from-white/10 to-transparent">
            <img src={image} className="absolute inset-0 h-full w-full object-cover" alt={title} />
          </div>
        </div>
        <div className="mt-2 sm:mt-3 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-sm sm:text-lg font-semibold truncate">{title}</div>
            <div className="text-xs text-white/50">BYTRON</div>
          </div>
          <div className="rounded-full border-2 px-3 sm:px-4 py-1 text-sm ml-2 flex-shrink-0" style={{ borderColor: theme.accent }}>
            {price}
          </div>
        </div>
      </div>
    </Link>
  );
}

function Catalog() {
  const items = [
    { title: "Crash", price: "$100", image: "/1xbet-crash.png" },
    { title: "Aviator Spribe Stake & 1win", price: "$100", image: "/1win-aviator.png" },
    { title: "LuckyJet", price: "$100", image: "/1win-luckyjet.png" },
    { title: "Mines", price: "$125", image: "/mines.png" },
    { title: "Mostbet Aviator Spribe", price: "$100", image: "/mostbet-aviator.png" },
    { title: "Apple of Fortune", price: "$100", image: "/apple-of-fortune.png" },
    { title: "Thimbles", price: "$100", image: "/thimbles.png" },
    { title: "Wild West Gold", price: "$100", image: "/wild-west-gold.png" },
    { title: "Higher vs Lower", price: "$100", image: "/higher-vs-lower.png" },
    { title: "Dragons Gold", price: "$100", image: "/dragons-gold.png" },
  ];

  return (
    <section id="catalog" className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
      <SectionTitle subtitle="Available Tools">
        BYTRON <Grad>Product Suite</Grad>
      </SectionTitle>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
        {items.map((it) => (
          <ProductCard key={it.title} {...it} />
        ))}
      </div>
    </section>
  );
}

/* ============================================
   VIDEO SECTION
============================================ */
function VideoSection() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-16">
      <SectionTitle subtitle="Watch BYTRON Aviator Predictor in Action">
        Live <Grad>Demonstration</Grad>
      </SectionTitle>

      <div className="max-w-3xl mx-auto">
        <Card glow className="overflow-hidden">
          <div className="aspect-video">
            <iframe
              src="https://www.youtube.com/embed/of9J1gFBHGA?rel=0&modestbranding=1"
              title="BYTRON Aviator Predictor Tools Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </Card>
      </div>
    </section>
  );
}

/* ============================================
   REVIEWS
============================================ */
function Reviews() {
  return (
    <section id="reviews" className="mx-auto max-w-7xl px-4 py-12 sm:py-16 text-center">
      <SectionTitle subtitle="Real Results">
        User <Grad>Testimonials</Grad>
      </SectionTitle>

      <div className="flex justify-center">
        <Button
          variant="outline"
          className="text-sm sm:text-lg px-6 sm:px-8 py-3 sm:py-4 flex items-center gap-2 sm:gap-3"
          onClick={() => window.open(TELEGRAM.reviews, "_blank")}
        >
          <img src="/icons/telegram.svg" className="h-4 w-4 sm:h-5 sm:w-5" alt="" />
          Read 500+ Success Stories
        </Button>
      </div>
    </section>
  );
}

/* ============================================
   CTA
============================================ */
function CTA() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:py-20 text-center">
      <Card glow className="p-8 sm:p-12">
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
           <Grad>Download Aviator Predictor APK Now</Grad>
        </h3>
        <p className="mx-auto max-w-xl text-white/70 mb-6">
          Join 30,000+ users with our aviator hack APK. Get stake aviator hack, 1win hack, 1xbet crash hack, and crash game predictor - all in one download.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button variant="primary" className="px-8 py-3 text-lg" onClick={() => window.open(TELEGRAM.chat, "_blank")}>
            <Download className="h-5 w-5 mr-2" />
            Download Aviator Hack APK
          </Button>
          <Button variant="outline" className="px-6 py-3" onClick={() => window.open(TELEGRAM.channel, "_blank")}>
            Join Channel
          </Button>
        </div>
      </Card>
    </section>
  );
}

/* ============================================
   FOOTER
============================================ */
function Footer() {
  return (
    <footer className="mx-auto max-w-7xl px-4 pb-8 sm:pb-12 pt-8 border-t border-white/10">
      <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-3">
            <img src="/logo.png" alt="Aviator Predictor APK" className="h-8 w-8 rounded-lg" />
            <span className="font-bold text-lg">{theme.name}</span>
          </div>
          <p className="text-sm text-white/60">
            Download Aviator Predictor APK - Best aviator hack and crash game predictor. Stake aviator hack, 1win hack, 1xbet crash hack for 30K+ users worldwide.
          </p>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Aviator Hack APK</h4>
          <ul className="space-y-2 text-sm text-white/60">
            <li><a className="hover:text-white transition" href="#features">Aviator Predictor</a></li>
            <li><a className="hover:text-white transition" href="#demo">Crash Predictor Demo</a></li>
            <li><a className="hover:text-white transition" href="#catalog">All Hack Tools</a></li>
            <li><a className="hover:text-white transition" href="#faq">FAQ</a></li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Supported Platforms</h4>
          <ul className="space-y-2 text-sm text-white/60">
            <li>Stake Aviator Hack</li>
            <li>1win Hack</li>
            <li>1xbet Crash Hack</li>
            <li>Stake Crash Predictor</li>
          </ul>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Download Aviator Hack</h4>
          <ul className="space-y-2 text-sm text-white/60">
            <li className="flex items-center gap-2">
              <img src="/icons/telegram.svg" className="h-4 w-4 opacity-60" alt="" />
              <a className="hover:text-white transition" href={TELEGRAM.chat} target="_blank" rel="noopener noreferrer">Get APK Now</a>
            </li>
            <li className="flex items-center gap-2">
              <img src="/icons/telegram.svg" className="h-4 w-4 opacity-60" alt="" />
              <a className="hover:text-white transition" href={TELEGRAM.channel} target="_blank" rel="noopener noreferrer">Updates Channel</a>
            </li>
            <li className="flex items-center gap-2">
              <img src="/icons/instagram.svg" className="h-4 w-4 opacity-60" alt="" />
              <a className="hover:text-white transition" href={INSTAGRAM.url} target="_blank" rel="noopener noreferrer">Instagram</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/50">
        <div>© {new Date().getFullYear()} Aviator Predictor APK - BYTRON. Aviator hack, stake hack, 1xbet crash hack.</div>
        <div className="flex items-center gap-4">
          <span>190+ Platforms Supported</span>
          <span>•</span>
          <span>Aviator Signal App 24/7</span>
        </div>
      </div>
    </footer>
  );
}

/* ============================================
   MAIN APP
============================================ */
export default function App() {
  const [serverStatusOpen, setServerStatusOpen] = useState(false);

  return (
    <div
      className="min-h-screen w-full text-white antialiased"
      style={{ background: `linear-gradient(180deg, ${theme.bgFrom}, ${theme.bgTo})` }}
    >
      <FallingMoney />
      <GlassNav onOpenServerStatus={() => setServerStatusOpen(true)} />
      <Hero />
      <Features />
      <div id="demo">
        <VideoSection />
      </div>
      <HistoryChart />
      <HowItWorks />
      <Catalog />
      <Reviews />
      <div id="faq">
        <FAQ />
      </div>
      <CTA />
      <Footer />

      <ServerStatusModal isOpen={serverStatusOpen} onClose={() => setServerStatusOpen(false)} />
    </div>
  );
}